import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Lock, Play, CheckCircle, Award, Unlock, Loader2, Sparkles, Settings, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LightBackground from '@/components/LightBackground';
import { getAcademyById } from '@/data/academyData';
import { useModuleProgress } from '@/hooks/useModuleProgress';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { useFreemiumAccess } from '@/hooks/useFreemiumAccess';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import Certificate from '@/components/Certificate';
import LockedContentModal from '@/components/LockedContentModal';
import { toast } from 'sonner';
import { LanguageSelector } from '@/components/LanguageSelector';
import { TranslatedText } from '@/components/TranslatedText';

type AcademyStatus = 'active' | 'maintenance' | 'coming_soon';

const Academy = () => {
  const { academyId } = useParams<{ academyId: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const academy = getAcademyById(academyId || '');
  const { isModuleCompleted, getAcademyProgress, isLoading } = useModuleProgress();
  const { 
    isModuleUnlocked, 
    getUnlockedModulesInAcademy, 
    isLifetime,
    isLoading: accessLoading,
    unlockedModuleCount,
    totalModules
  } = useModuleAccess();
  const [showCertificate, setShowCertificate] = useState(false);
  const [memberName, setMemberName] = useState('Member');
  
  const [showLockedModal, setShowLockedModal] = useState(false);
  const [lockReason, setLockReason] = useState<'freemium' | 'monthly_incomplete'>('freemium');
  const [previousModuleNumber, setPreviousModuleNumber] = useState<number | undefined>(undefined);
  const [academyStatus, setAcademyStatus] = useState<AcademyStatus>('active');
  const [statusLoading, setStatusLoading] = useState(true);
  const { 
    hasFullAccess, 
    isContentFree, 
    canAccessModule, 
    clientType, 
    loading: freemiumLoading,
    isMonthlyPlan,
    getNextRequiredModule,
    completedModules
  } = useFreemiumAccess();

  // Fetch academy status from database
  useEffect(() => {
    const fetchAcademyStatus = async () => {
      if (!academyId) return;
      
      try {
        const { data, error } = await supabase
          .from('academies')
          .select('status')
          .eq('id', academyId)
          .maybeSingle();
        
        if (academy?.comingSoon) {
          setAcademyStatus('coming_soon');
        } else if (data?.status) {
          setAcademyStatus(data.status as AcademyStatus);
        }
      } catch (error) {
        console.error('Error fetching academy status:', error);
      } finally {
        setStatusLoading(false);
      }
    };
    
    fetchAcademyStatus();
  }, [academyId]);

  // Fetch member name
  useEffect(() => {
    const fetchMemberName = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('clients')
        .select('name')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data?.name) {
        setMemberName(data.name);
      }
    };
    fetchMemberName();
  }, [user]);

  if (!academy) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <p className="text-slate-900">Academy not found</p>
      </div>
    );
  }

  if (accessLoading || isLoading || freemiumLoading || statusLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Show blocked screen for non-admins when academy is not active
  if (academyStatus !== 'active' && !isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900 overflow-hidden">
        <LightBackground variant="subtle" />
        
        <header className="relative z-50 px-6 py-4 border-b border-slate-200">
          <div className="max-w-7xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-slate-600 hover:text-blue-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </header>

        <main className="relative z-10 px-6 py-24">
          <div className="max-w-xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-12 rounded-2xl border border-slate-200 shadow-xl"
            >
              {academyStatus === 'maintenance' ? (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                    <Settings className="w-10 h-10 text-amber-600 animate-spin" style={{ animationDuration: '3s' }} />
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold mb-4 text-slate-900">
                    Under Maintenance
                  </h1>
                  <p className="text-slate-600 mb-6">
                    We're currently updating <span className="text-blue-600 font-medium">{academy.name}</span> to bring you even better content. 
                    Please check back soon!
                  </p>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-100 flex items-center justify-center">
                    <Clock className="w-10 h-10 text-blue-600" />
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold mb-4 text-slate-900">
                    Coming Soon
                  </h1>
                  <p className="text-slate-600 mb-6">
                    <span className="text-blue-600 font-medium">{academy.name}</span> is being prepared and will be available soon. 
                    Stay tuned for the launch!
                  </p>
                </>
              )}
              
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                size="lg"
              >
                Return to Dashboard
              </Button>
            </motion.div>
          </div>
        </main>
      </div>
    );
  }


  const unlockedInThisAcademy = getUnlockedModulesInAcademy(academy.id);
  const progress = getAcademyProgress(academy.id);
  const progressPercent = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
  const isCompleteAcademy = progress.completed >= progress.total && progress.total > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900 overflow-hidden">
      <LightBackground variant="subtle" />

      {/* Header */}
      <header className="relative z-50 px-6 py-4 border-b border-slate-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-slate-600 hover:text-blue-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <TranslatedText>Back</TranslatedText>
          </Button>
          
          <div className="flex items-center gap-2">
            <LanguageSelector variant="ghost" className="text-slate-600 hover:text-slate-900" />
            {isCompleteAcademy && (
              <Button
                variant="outline"
                onClick={() => setShowCertificate(true)}
                className="border-blue-300 text-blue-600 hover:bg-blue-50"
              >
                <Award className="w-4 h-4 mr-2" />
                <TranslatedText>View Certificate</TranslatedText>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Academy Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            {academy.tagline && (
              <span className="text-blue-600 text-sm font-medium uppercase tracking-wider mb-2 block">
                <TranslatedText cacheKey={`academy_tagline_${academy.id}`}>{academy.tagline}</TranslatedText>
              </span>
            )}
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900">
              <TranslatedText cacheKey={`academy_name_${academy.id}`}>{academy.name}</TranslatedText>
            </h1>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-6">
              <TranslatedText cacheKey={`academy_desc_${academy.id}`}>{academy.description}</TranslatedText>
            </p>
            
            {/* Progress Bar */}
            <div className="max-w-md mx-auto">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-slate-500"><TranslatedText>Progress</TranslatedText></span>
                <span className="text-blue-600 font-medium">{progress.completed}/{progress.total} <TranslatedText>modules</TranslatedText></span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                />
              </div>
              
              {isCompleteAcademy && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4"
                >
                  <Button
                    onClick={() => setShowCertificate(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white gap-2 rounded-xl"
                  >
                    <Award className="w-4 h-4" />
                    <TranslatedText>Download Your Certificate</TranslatedText>
                  </Button>
                </motion.div>
              )}
            </div>
            
            {/* Module Access Info for Monthly Plan */}
            {isMonthlyPlan && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-6 p-4 bg-white rounded-xl border border-slate-200 shadow-sm text-center"
              >
                <p className="text-sm text-slate-600 mb-2">
                  <Unlock className="w-4 h-4 inline mr-1" />
                  <TranslatedText>Completion-Based Unlock System</TranslatedText>
                </p>
                <p className="text-xs text-slate-500 mb-3">
                  <TranslatedText>Complete 100% of each module to unlock the next one.</TranslatedText>{' '}
                  <TranslatedText>Completed in this academy</TranslatedText>: {completedModules.filter(m => m.academyId === academyId).length}/{academy.modules.length}
                </p>
              </motion.div>
            )}
          </motion.div>

          {/* Upgrade Banner for Free Users */}
          {!hasFullAccess && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200 text-center"
            >
              <Sparkles className="w-8 h-8 text-blue-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                <TranslatedText>Upgrade to Access All Modules</TranslatedText>
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                You're viewing with free access. Subscribe to unlock the full curriculum!
              </p>
              <Button
                onClick={() => setShowLockedModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                size="lg"
              >
                Subscribe Now
              </Button>
            </motion.div>
          )}

          {/* Modules Grid */}
          <div className="space-y-4">
            {academy.modules.map((module, index) => {
              const isUnlocked = isModuleUnlocked(academy.id, module.id);
              const isCompleted = isModuleCompleted(academy.id, module.id);
              // Check if this module is free (Module 1 of first academy)
              const isFreeModule = isContentFree(academy.id, module.id);
              // Use the centralized access check
              const canAccess = canAccessModule(academy.id, module.id);

              return (
                <motion.div
                  key={module.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  onClick={() => {
                    if (canAccess) {
                      navigate(`/academy/${academyId}/module/${module.id}`);
                    } else if (isMonthlyPlan) {
                      // Monthly user - show completion requirement
                      const requiredModule = getNextRequiredModule(academy.id, module.id);
                      setPreviousModuleNumber(requiredModule || module.id - 1);
                      setLockReason('monthly_incomplete');
                      setShowLockedModal(true);
                    } else if (!hasFullAccess) {
                      // Freemium user - show subscribe modal
                      setLockReason('freemium');
                      setShowLockedModal(true);
                    }
                  }}
                  className={`
                    relative p-6 rounded-xl transition-all duration-300 bg-white border shadow-sm
                    ${isCompleted
                      ? 'border-green-300 cursor-pointer hover:shadow-md'
                      : canAccess 
                        ? 'border-slate-200 hover:border-blue-300 cursor-pointer hover:shadow-md' 
                        : 'border-slate-200 opacity-60 cursor-pointer hover:opacity-80'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`
                        w-12 h-12 rounded-full flex items-center justify-center
                        ${isCompleted
                          ? 'bg-green-100 text-green-600'
                          : canAccess 
                            ? 'bg-blue-100 text-blue-600' 
                            : 'bg-slate-100 text-slate-400'
                        }
                      `}>
                        {isCompleted ? (
                          <CheckCircle className="w-6 h-6" />
                        ) : canAccess ? (
                          <Play className="w-5 h-5" />
                        ) : (
                          <Lock className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">
                            <TranslatedText>Module</TranslatedText> {module.id}
                          </span>
                          {isFreeModule && !hasFullAccess && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-600">
                              <TranslatedText>FREE</TranslatedText>
                            </span>
                          )}
                        </div>
                        <h3 className={`text-lg font-semibold ${canAccess ? 'text-slate-900' : 'text-slate-500'}`}>
                          <TranslatedText cacheKey={`module_title_${academy.id}_${module.id}`}>{module.title}</TranslatedText>
                        </h3>
                      </div>
                    </div>

                    <div className={`text-sm font-medium ${isCompleted ? 'text-green-600' : canAccess ? 'text-blue-600' : 'text-slate-400'}`}>
                      {isCompleted ? <TranslatedText>Completed ✓</TranslatedText> : canAccess ? <TranslatedText>Start →</TranslatedText> : <Lock className="w-4 h-4" />}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </main>

      {/* Certificate Modal */}
      <AnimatePresence>
        {showCertificate && (
          <Certificate
            memberName={memberName}
            academyName={academy.name}
            completionDate={new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
            onClose={() => setShowCertificate(false)}
          />
        )}
      </AnimatePresence>

      {/* Locked Content Modal */}
      <LockedContentModal
        isOpen={showLockedModal}
        onClose={() => setShowLockedModal(false)}
        contentType="module"
        lockReason={lockReason}
        previousModuleNumber={previousModuleNumber}
      />
    </div>
  );
};

export default Academy;